import React, { useState } from 'react';

interface BetaSignupFormProps {
  onSuccess?: () => void;
}

export default function BetaSignupForm({ onSuccess }: BetaSignupFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    platform: 'android' as 'ios' | 'android',
    experienceYears: '0-1' as '0-1' | '2-5' | '6-10' | '10+'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch('https://app.controller-app.com/api/v1/beta-signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        setFormData({
          name: '',
          email: '',
          platform: 'android',
          experienceYears: '0-1'
        });
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setError(data.message || 'Failed to sign up. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      console.error('Beta signup error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (success) {
    return (
      <div className="beta-signup-form">
        <div style={{ 
          padding: '2rem', 
          textAlign: 'center',
          background: 'rgba(168, 85, 247, 0.1)',
          borderRadius: '0.75rem',
          border: '1px solid rgba(168, 85, 247, 0.3)'
        }}>
          <h3 style={{ color: '#A855F7', marginBottom: '1rem' }}>✓ Successfully Signed Up!</h3>
          <p style={{ color: '#6B7280' }}>
            Thank you for signing up for the {formData.platform === 'android' ? 'Android' : 'iOS'} beta! 
            We'll be in touch soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form className="beta-signup-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="name" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
          Name *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          className="form-input"
          value={formData.name}
          onChange={handleChange}
          required
          placeholder="Enter your name"
        />
      </div>

      <div className="form-group">
        <label htmlFor="email" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
          Email *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          className="form-input"
          value={formData.email}
          onChange={handleChange}
          required
          placeholder="your.email@example.com"
        />
      </div>

      <div className="form-group">
        <label htmlFor="platform" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
          Platform *
        </label>
        <select
          id="platform"
          name="platform"
          className="form-select"
          value={formData.platform}
          onChange={handleChange}
          required
          style={{
            opacity: formData.platform === 'ios' ? 0.5 : 1,
            cursor: formData.platform === 'ios' ? 'not-allowed' : 'pointer'
          }}
          disabled={formData.platform === 'ios'}
        >
          <option value="android">Android (Open)</option>
          <option value="ios" disabled>iOS (Closed)</option>
        </select>
        {formData.platform === 'ios' && (
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#6B7280', 
            marginTop: '0.5rem',
            fontStyle: 'italic'
          }}>
            iOS beta signups are currently closed.
          </p>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="experienceYears" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
          Experience Level *
        </label>
        <select
          id="experienceYears"
          name="experienceYears"
          className="form-select"
          value={formData.experienceYears}
          onChange={handleChange}
          required
        >
          <option value="0-1">0-1 years</option>
          <option value="2-5">2-5 years</option>
          <option value="6-10">6-10 years</option>
          <option value="10+">10+ years</option>
        </select>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <button
        type="submit"
        className="beta-button"
        disabled={loading || formData.platform === 'ios'}
        style={{
          width: '100%',
          marginTop: '1rem',
          opacity: formData.platform === 'ios' ? 0.5 : 1,
          cursor: formData.platform === 'ios' ? 'not-allowed' : 'pointer'
        }}
      >
        {loading ? 'Signing Up...' : 'Sign Up for Beta'}
      </button>
    </form>
  );
}

